"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  LockKeyhole,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { FaDiscord, FaGithub, FaGoogle } from "react-icons/fa";
import { useSession } from "@/components/session-provider";
import { ThemeProvider } from "@/components/theme-provider";
import { PanelShell } from "@/components/panel-shell";
import { StaffShell } from "@/components/staff-shell";
import { AdminShell } from "@/components/admin-shell";
import { Busy, ErrorNotice, Field } from "@/components/ui";
import { ApiError, errorMessage, mutation } from "@/lib/api";
import { useResource } from "@/lib/use-resource";

type LoginProvider = "discord" | "google" | "github";

function ProviderMark({ provider }: { provider: LoginProvider }) {
  const Icon = provider === "discord" ? FaDiscord : provider === "google" ? FaGoogle : FaGithub;
  return <Icon className={`provider-mark provider-mark-${provider}`} aria-hidden="true" />;
}

const providerLabels: Record<LoginProvider, string> = {
  discord: "Discord",
  google: "Google",
  github: "GitHub",
};

const oauthErrors: Record<string, string> = {
  unavailable: "That sign-in provider is not available right now.",
  unverified: "The provider did not return a verified email address.",
  unlinked: "No Velnqo account matches that provider's verified email address.",
  taken: "That provider account is already connected to another Velnqo account.",
  error: "The provider sign-in could not be completed. Please try again.",
};

function SignIn() {
  const session = useSession();
  const router = useRouter();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [secondFactor, setSecondFactor] = useState<{ login: string; password: string } | null>(null);
  const providers = useResource<{ providers: Record<LoginProvider, boolean> }>("/auth/providers");
  const refreshSession = session.refresh;

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const status = query.get("oauth");
    if (!status) return;
    const provider = query.get("provider") as LoginProvider | null;
    const providerName = provider && providerLabels[provider] ? providerLabels[provider] : "Provider";
    const applyResult = window.setTimeout(() => {
      query.delete("oauth");
      query.delete("provider");
      const remaining = query.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${remaining ? `?${remaining}` : ""}${window.location.hash}`);
      if (status === "success") void refreshSession();
      else setError(`${providerName}: ${oauthErrors[status] ?? oauthErrors.error}`);
    }, 0);
    return () => window.clearTimeout(applyResult);
  }, [refreshSession]);

  async function startOAuth(provider: LoginProvider) {
    setPending(true);
    setError("");
    try {
      const result = await mutation<{ url: string }>(`/auth/oauth/${provider}/start`, {});
      const url = new URL(result.url);
      if (url.protocol !== "https:") throw new Error("Invalid OAuth redirect.");
      window.location.assign(url.href);
    } catch (error) {
      setError(errorMessage(error));
      setPending(false);
    }
  }

  return (
    <main className="sign-in">
      <div className="sign-in-brand">
        <Zap size={26} />
        <span>{session.panel?.mode === "workspace" ? session.panel.workspace.name : "Velnqo Panel"}</span>
      </div>
      <form
        className="sign-in-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setPending(true);
          setError("");
          const data = new FormData(event.currentTarget);
          try {
            if (secondFactor) {
              await session.login(secondFactor.login, secondFactor.password, String(data.get("code") || ""));
            } else {
              const login = String(data.get("login"));
              const password = String(data.get("password"));
              try {
                await session.login(login, password);
              } catch (error) {
                if (error instanceof ApiError && error.code === "second_factor_required") {
                  setSecondFactor({ login, password });
                  return;
                }
                throw error;
              }
            }
            router.replace("/");
          } catch (error) {
            setError(errorMessage(error));
          } finally {
            setPending(false);
          }
        }}
      >
        <div className="sign-in-heading">
          {secondFactor ? <ShieldCheck size={22} /> : <LockKeyhole size={22} />}
          <h1>{secondFactor ? "Verify your identity" : "Sign in"}</h1>
        </div>
        <ErrorNotice
          message={error || session.error}
          retry={session.error ? () => void session.refresh() : undefined}
        />
        {!secondFactor ? <>
          <Field label="Email or username"><input name="login" autoComplete="username" required autoFocus /></Field>
          <Field label="Password"><input name="password" type="password" autoComplete="current-password" required /></Field>
        </> : <>
          <p className="sign-in-account">Signing in as <strong>{secondFactor.login}</strong></p>
          <p className="sign-in-help">Enter the code from your authenticator app or one of your recovery codes.</p>
          <Field label="Authenticator or recovery code"><input name="code" autoComplete="one-time-code" inputMode="text" maxLength={64} required autoFocus spellCheck={false} autoCapitalize="none" /></Field>
        </>}
        <button className="button button-primary" disabled={pending}>
          {pending ? "Please wait..." : secondFactor ? "Verify and sign in" : "Sign in"}
          <ArrowRight size={17} />
        </button>
        {secondFactor ? <button className="sign-in-back" type="button" disabled={pending} onClick={() => { setSecondFactor(null); setError(""); }}><ArrowLeft size={14} />Use a different account</button> : <>
          <div className="sign-in-divider"><span>or continue with</span></div>
          <div className="provider-buttons">
            {(["discord", "google", "github"] as LoginProvider[]).map((provider) => {
              const enabled = !!providers.data?.providers[provider];
              const unavailable = !providers.loading && !enabled;
              return <button key={provider} className="button provider-button" type="button" disabled={pending || providers.loading || unavailable} aria-label={`Continue with ${providerLabels[provider]}`} aria-busy={providers.loading || undefined} onClick={() => void startOAuth(provider)} title={unavailable ? `${providerLabels[provider]} sign-in is not configured` : `Continue with ${providerLabels[provider]}`}><ProviderMark provider={provider} /><span>{providerLabels[provider]}</span></button>;
            })}
          </div>
        </>}
      </form>
      <div className="sign-in-footer">
        <ShieldCheck size={14} />
        Velnqo server management
      </div>
    </main>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const pathname = usePathname();
  if (session.loading)
    return (
      <main className="boot-screen">
        <Zap size={28} />
        <span>Velnqo Panel</span>
        <Busy label="Connecting" />
      </main>
    );
  if (!session.panel)
    return (
      <main className="boot-screen">
        <LockKeyhole size={28} />
        <span>Workspace unavailable</span>
        <ErrorNotice message={session.error || "This hostname is not assigned to an active Velnqo workspace."} retry={() => void session.refresh()} />
      </main>
    );
  if (!session.user) return <SignIn />;
  if (session.panel.mode === "workspace" && session.tenant?.slug !== session.panel.workspace.slug)
    return (
      <main className="boot-screen">
        <LockKeyhole size={28} />
        <span>Workspace access denied</span>
        <ErrorNotice message="This account does not belong to this workspace." />
        <button className="button" type="button" onClick={() => void session.logout()}>Sign out</button>
      </main>
    );
  return (
    <ThemeProvider key={session.user.id}>
      {pathname.startsWith("/staff") ? <StaffShell>{children}</StaffShell> : pathname.startsWith("/admin") ? <AdminShell>{children}</AdminShell> : <PanelShell>{children}</PanelShell>}
    </ThemeProvider>
  );
}
